// wishlistStore.ts
//
// User's wishlist of books — backed by D2P /api/d2p/user/wishlist.
// Same wishlist appears on the d2p website and in KleverBook (a book heart-ed
// in either place shows up in both).
//
// Optimistic UX: add/remove flips local state immediately, then syncs in the
// background. On failure we roll back and show a dynamic-island error.

import { makeAutoObservable, runInAction } from 'mobx';
import { hitApi } from '../database/services/ApiHelper';
import { API } from '../database/apis';
import { Book } from '../types';
import { userStore } from './userStore';
import localforage from 'localforage';

export interface WishlistEntry {
  _id: string; // bookId
  addedAt: string;
  book: Book;
}

class WishlistStore {
  entries: WishlistEntry[] = [];
  loading = false;
  // Per-book mutation flag so the heart button can show a spinner without
  // freezing the whole list.
  mutating: Record<string, boolean> = {};

  constructor() {
    makeAutoObservable(this);
    this.hydrate();
  }

  async hydrate() {
    try {
      const saved = await localforage.getItem<string>('wishlist');
      if (saved) {
        runInAction(() => {
          this.entries = JSON.parse(saved);
        });
      }
    } catch (e) {
      console.log('Wishlist hydration error:', e);
    }
  }

  async persist() {
    try {
      await localforage.setItem('wishlist', JSON.stringify(this.entries));
    } catch (e) {
      console.log('Wishlist persist error:', e);
    }
  }

  has(bookId: string): boolean {
    return this.entries.some((e) => e._id === bookId);
  }

  async load() {
    if (!userStore.user || !userStore.authToken) return;
    this.loading = true;
    try {
      const res = await hitApi({
        endpoint: API.WISHLIST,
        method: 'GET',
        token: userStore.authToken,
      });
      const items: any[] = Array.isArray(res?.wishlist) ? res.wishlist : [];
      runInAction(() => {
        this.entries = items.map((item: any): WishlistEntry => ({
          _id: item.book.id,
          addedAt: item.addedAt,
          book: {
            _id: item.book.id,
            title: item.book.title,
            authorName: item.book.author ?? 'Anonymous',
            category: item.book.category,
            categoryName: item.book.category,
            description: item.book.description ?? '',
            coverImage: item.book.coverUrl,
          },
        }));
        this.loading = false;
      });
      await this.persist();
    } catch (e) {
      runInAction(() => { this.loading = false; });
    }
  }

  async add(book: Book): Promise<boolean> {
    if (!userStore.authToken || this.has(book._id)) return false;

    // Optimistic update
    const optimistic: WishlistEntry = {
      _id: book._id,
      addedAt: new Date().toISOString(),
      book,
    };
    runInAction(() => {
      this.entries = [optimistic, ...this.entries];
      this.mutating[book._id] = true;
    });
    this.persist();

    try {
      await hitApi({
        endpoint: API.WISHLIST,
        method: 'POST',
        body: { bookId: book._id },
        token: userStore.authToken,
      });
      runInAction(() => { delete this.mutating[book._id]; });
      return true;
    } catch (e) {
      // Roll back
      runInAction(() => {
        this.entries = this.entries.filter((entry) => entry._id !== book._id);
        delete this.mutating[book._id];
      });
      this.persist();
      return false;
    }
  }

  async remove(bookId: string): Promise<boolean> {
    if (!userStore.authToken || !this.has(bookId)) return false;

    // Snapshot for rollback
    const snapshot = this.entries.find((e) => e._id === bookId);
    runInAction(() => {
      this.entries = this.entries.filter((e) => e._id !== bookId);
      this.mutating[bookId] = true;
    });
    this.persist();

    try {
      await hitApi({
        endpoint: API.WISHLIST_REMOVE(bookId),
        method: 'DELETE',
        token: userStore.authToken,
      });
      runInAction(() => { delete this.mutating[bookId]; });
      return true;
    } catch (e) {
      if (snapshot) {
        runInAction(() => {
          this.entries = [snapshot, ...this.entries];
          delete this.mutating[bookId];
        });
        this.persist();
      } else {
        runInAction(() => { delete this.mutating[bookId]; });
      }
      return false;
    }
  }

  async toggle(book: Book): Promise<boolean> {
    return this.has(book._id) ? this.remove(book._id) : this.add(book);
  }

  async logout() {
    runInAction(() => {
      this.entries = [];
      this.mutating = {};
    });
    await localforage.removeItem('wishlist');
  }
}

export const wishlistStore = new WishlistStore();
